// src/ui/screens/post/AllUsersScreen.jsx
//
// Full directory of users, reached via "See more" on either recommendation
// rail. Reuses the existing row-based UserItem so subscribe logic, badges,
// and profile navigation stay identical to the rest of the app.
//
// Mirrors the Flutter AllUsersScreen: circular back button + "More channels"
// title, and a users-only search bar (no history) above the list.
//
// Desktop (>= 1024px): content is centered in a fixed-width column with
// subtle side borders, roomier header/search, row hover states, pointer
// cursors and a slim scrollbar. Mobile layout is unchanged.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  getDocs,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import UserItem from '../../../components/UserItem';

const BG = '#08090B';
const ORANGE = '#FF8C08';
const CARD = '#23262C';
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 450;

// Desktop-only rules. Wrapped in a media query so nothing changes on mobile.
// !important is needed to override the existing inline styles.
const DESKTOP_CSS = `
.search-channels-input::placeholder { color: rgba(255,255,255,0.28); font-weight: 400; }

.au-shell { width: 100%; }

@media (min-width: 1024px) {
  .au-shell {
    max-width: 760px;
    border-left: 1px solid rgba(255,255,255,0.06);
    border-right: 1px solid rgba(255,255,255,0.06);
  }
  .au-topbar {
    padding: 20px 28px 16px 24px !important;
  }
  .au-title {
    font-size: 20px !important;
    margin-left: 14px !important;
  }
  .au-back, .au-refresh, .au-clear { cursor: pointer; transition: background-color 0.15s ease, color 0.15s ease; }
  .au-back:hover { background-color: #2E323A !important; }
  .au-refresh { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
  .au-refresh:hover { background-color: rgba(255,255,255,0.08); color: #fff; }
  .au-clear:hover { background-color: rgba(255,255,255,0.32) !important; }
  .au-searchwrap {
    padding: 0 28px 16px 24px !important;
  }
  .au-searchbox {
    height: 48px !important;
    border: 1px solid transparent;
    transition: border-color 0.15s ease;
  }
  .au-searchbox:focus-within { border-color: rgba(255,140,8,0.55); }
  .au-list {
    padding: 4px 12px 24px 12px;
  }
  .au-row {
    border-radius: 12px;
    transition: background-color 0.15s ease;
  }
  .au-row:hover { background-color: rgba(255,255,255,0.04); }
  .au-list { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.18) transparent; }
  .au-list::-webkit-scrollbar { width: 8px; }
  .au-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.18); border-radius: 4px; }
  .au-list::-webkit-scrollbar-track { background: transparent; }
}
`;

export default function AllUsersScreen() {
  const navigate = useNavigate();
  const [userDocs, setUserDocs] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // ── Search — users only, no history ─────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [loadingSearchUsers, setLoadingSearchUsers] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState([]);

  const scrollRef = useRef(null);
  const lastDocRef = useRef(null);
  const debounceRef = useRef(null);
  const searchSeq = useRef(0); // drops results from outdated searches

  const loadUsers = useCallback(async () => {
    setIsLoadingInitial(true);
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      const snap = await getDocs(q);
      setUserDocs(snap.docs);
      setHasMore(snap.docs.length === PAGE_SIZE);
      if (snap.docs.length > 0) lastDocRef.current = snap.docs[snap.docs.length - 1];
    } catch (e) {
      console.error('AllUsersScreen load error:', e);
    } finally {
      setIsLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchSeq.current += 1;
    };
  }, []);

  const loadMoreUsers = useCallback(async () => {
    if (isLoadingMore || !hasMore || userDocs.length === 0) return;
    setIsLoadingMore(true);
    try {
      const q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      if (snap.docs.length > 0) {
        setUserDocs((prev) => [...prev, ...snap.docs]);
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error('AllUsersScreen load more error:', e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, userDocs]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) {
      loadMoreUsers();
    }
  }, [loadMoreUsers]);

  const handleRefresh = async () => {
    setUserDocs([]);
    setHasMore(true);
    await loadUsers();
  };

  // ── Query handling — mirrors the Flutter screen's _onQueryChanged /
  // _searchUsers, minus history tracking. ─────────────────────────────────

  const searchUsers = async (q) => {
    const seq = ++searchSeq.current;
    setLoadingSearchUsers(true);
    try {
      const lower = q.toLowerCase();
      const currentUid = auth.currentUser?.uid || '';

      const [nameSnap, userSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, 'users'),
            orderBy('displayNameLower'),
            startAt(lower),
            endAt(`${lower}\uf8ff`),
            limit(30)
          )
        ),
        getDocs(
          query(
            collection(db, 'users'),
            orderBy('username'),
            startAt(lower),
            endAt(`${lower}\uf8ff`),
            limit(30)
          )
        ),
      ]);

      if (seq !== searchSeq.current) return;

      const seen = new Set();
      const results = [];
      [...nameSnap.docs, ...userSnap.docs].forEach((d) => {
        if (d.id === currentUid || seen.has(d.id)) return;
        seen.add(d.id);
        results.push({ id: d.id, ...d.data() });
      });

      setSearchedUsers(results);
      setLoadingSearchUsers(false);
      setIsSearching(false);
    } catch (e) {
      console.error('AllUsersScreen search error:', e);
      if (seq === searchSeq.current) {
        setLoadingSearchUsers(false);
        setIsSearching(false);
      }
    }
  };

  const resetSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchSeq.current += 1;
    setSearchQuery('');
    setIsSearching(false);
    setLoadingSearchUsers(false);
    setSearchedUsers([]);
  };

  const onQueryChanged = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = value.trim();
    if (trimmed.length === 0) {
      resetSearch();
      return;
    }

    setIsSearching(true);

    debounceRef.current = setTimeout(() => {
      setSearchQuery(trimmed);
      searchUsers(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  };

  const clearSearch = () => {
    setSearchText('');
    resetSearch();
  };

  const currentUid = auth.currentUser?.uid || '';
  const users = userDocs.filter((d) => d.id !== currentUid).map((d) => ({ id: d.id, ...d.data() }));

  // ── Sections ─────────────────────────────────────────────────────────────

  const renderDefaultList = () => {
    if (isLoadingInitial) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: ORANGE, borderTopColor: 'transparent' }} />
        </div>
      );
    }
    if (users.length === 0) {
      return (
        <div className="flex items-center justify-center py-24">
          <span className="text-white/40 text-sm">No users found</span>
        </div>
      );
    }
    return (
      <div ref={scrollRef} onScroll={handleScroll} className="au-list overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
        {users.map((user, index) => (
          <div key={user.id}>
            <div className="au-row">
              <UserItem user={user} />
            </div>
            {index < users.length - 1 && <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />}
          </div>
        ))}
        {isLoadingMore && (
          <div className="flex justify-center py-5">
            <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: ORANGE, borderTopColor: 'transparent' }} />
          </div>
        )}
      </div>
    );
  };

  const renderSearchResults = () => {
    if (loadingSearchUsers || isSearching) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: ORANGE, borderTopColor: 'transparent' }} />
        </div>
      );
    }
    if (searchedUsers.length === 0) {
      return (
        <div className="flex items-center justify-center py-24 px-10">
          <div className="flex flex-col items-center text-center">
            <svg style={{ width: 56, height: 56, color: 'rgba(255,255,255,0.10)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.118a7.5 7.5 0 0114.998 0"
              />
            </svg>
            <div className="mt-4 font-semibold" style={{ color: 'rgba(255,255,255,0.40)', fontSize: 16 }}>
              {`No users found for "${searchQuery}"`}
            </div>
            <div className="mt-1.5" style={{ color: 'rgba(255,255,255,0.20)', fontSize: 13 }}>
              Try a different name or username
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="au-list overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
        {searchedUsers.map((user, index) => (
          <div key={user.id}>
            <div className="au-row">
              <UserItem user={user} />
            </div>
            {index < searchedUsers.length - 1 && <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex justify-center" style={{ backgroundColor: BG, height: '100vh' }}>
      <div className="au-shell flex flex-col" style={{ backgroundColor: BG, height: '100%' }}>
        {/* Top bar — circular back button + title, as in the Flutter screen */}
        <div
          className="au-topbar flex items-center"
          style={{
            backgroundColor: BG,
            padding: '10px 16px 10px 12px',
            paddingTop: 'calc(env(safe-area-inset-top, 0px) + 10px)',
          }}
        >
          <button
            onClick={() => navigate(-1)}
            className="au-back flex items-center justify-center text-white"
            style={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: CARD }}
          >
            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="au-title flex-1 text-white font-bold" style={{ fontSize: 16, marginLeft: 10 }}>
            More channels
          </h1>
          <button onClick={handleRefresh} className="au-refresh text-white/60">
            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {/* Search bar — users only, no history */}
        <div className="au-searchwrap" style={{ padding: '0 16px 10px 12px' }}>
          <div
            className="au-searchbox flex items-center"
            style={{ height: 44, backgroundColor: CARD, borderRadius: 12 }}
          >
            <svg
              style={{ width: 20, height: 20, marginLeft: 12, marginRight: 8, flexShrink: 0, color: 'rgba(255,255,255,0.35)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
            </svg>
            <input
              type="text"
              value={searchText}
              onChange={(e) => onQueryChanged(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                if (debounceRef.current) clearTimeout(debounceRef.current);
                const q = searchText.trim();
                if (q) {
                  setSearchQuery(q);
                  searchUsers(q);
                }
              }}
              placeholder="Search channels"
              enterKeyHint="search"
              autoComplete="off"
              className="search-channels-input flex-1 min-w-0 bg-transparent outline-none text-white font-medium"
              style={{ fontSize: 15, caretColor: ORANGE }}
            />
            {searchQuery || searchText ? (
              <button
                onClick={clearSearch}
                aria-label="Clear search"
                className="au-clear flex items-center justify-center text-white"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.20)',
                  marginRight: 8,
                  flexShrink: 0,
                }}
              >
                <svg style={{ width: 13, height: 13 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : (
              <div style={{ width: 12, flexShrink: 0 }} />
            )}
          </div>
          <style>{DESKTOP_CSS}</style>
        </div>

        {searchQuery ? renderSearchResults() : renderDefaultList()}
      </div>
    </div>
  );
}